import { Stack, Tab, Tabs, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import {
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
  useNavigate
} from '@tanstack/react-router';

import { useEffect } from 'react';

import { CampaignDetailsDialog } from '../components/CampaignDetailsDialog';
import {
  FAILED_CAMPAIGNS_BADGE_QUERY_KEY,
  FAILED_CAMPAIGNS_LAST_CHECKED_KEY
} from '../components/NavMenu';

type CampaignsSearch = {
  campaignId?: string;
  sendAtFrom?: string;
  sendAtTo?: string;
  status?: string;
};

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate({ from: Route.fullPath });
  const { campaignId } = Route.useSearch();
  const queryClient = useQueryClient();

  useEffect(() => {
    localStorage.setItem(
      FAILED_CAMPAIGNS_LAST_CHECKED_KEY,
      new Date().toISOString()
    );
    queryClient.invalidateQueries({
      queryKey: [FAILED_CAMPAIGNS_BADGE_QUERY_KEY]
    });
  }, [queryClient]);

  const tab = location.pathname.startsWith('/campaigns/messages')
    ? 'messages'
    : 'campaigns';

  const onCloseDialog = () =>
    navigate({ search: prev => ({ ...prev, campaignId: undefined }) });

  const onCampaignsTab = () => navigate({ to: '/campaigns' });
  const onMessagesTab = () => navigate({ to: '/campaigns/messages' });

  return (
    <Stack spacing={2} sx={{ height: '100%', p: 2 }}>
      <Typography variant='h5'>Email Campaigns</Typography>

      <Tabs value={tab}>
        <Tab
          data-testid='campaignsTab'
          label='Campaigns'
          onClick={onCampaignsTab}
          value='campaigns'
        />
        <Tab
          data-testid='messagesTab'
          label='Messages'
          onClick={onMessagesTab}
          value='messages'
        />
      </Tabs>

      <Outlet />

      <CampaignDetailsDialog
        campaignId={campaignId ?? null}
        onClose={onCloseDialog}
      />
    </Stack>
  );
};

export const Route = createFileRoute('/campaigns')({
  beforeLoad: () => {
    if (localStorage.getItem('isAdmin') !== '1') {
      throw redirect({ to: '/organizations' });
    }
  },
  component: Layout,
  validateSearch: (search: Record<string, unknown>): CampaignsSearch => ({
    campaignId:
      typeof search.campaignId === 'string' ? search.campaignId : undefined,
    sendAtFrom:
      typeof search.sendAtFrom === 'string' ? search.sendAtFrom : undefined,
    sendAtTo: typeof search.sendAtTo === 'string' ? search.sendAtTo : undefined,
    status: typeof search.status === 'string' ? search.status : undefined
  })
});
